import { Button } from '@mui/material';
import Rails from '@rails/ujs';
import Axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { RouteComponentProps } from 'react-router-dom';
import Select from 'react-select';
import DateRangeSelect from '../Common/DateRangeSelect';
import { ISelectOption, ProjectTaskData } from '../Common/types';
import { URLParamsReader } from '../Common/URLParamsReader';
import Paging from '../Notifications/Paging';
import TextSearchField from '../Notifications/TextSearchField';
import { AllPriorities } from './PriorityField';
import { AllProgressStatuses } from './StatusField';
import TableHeader from './TableHeader';
import TaskRow from './TaskRow';

declare function formatMoney(
  number: number,
  decPlaces: number,
  decSep: string,
  thouSep: string
): string;

function formatDate(date: Date) {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
}

// statuses selected when the screen is opened without filters: todo, waiting, in progress
const DEFAULT_STATUSES = AllProgressStatuses.filter((s) => s.value !== 'done' && s.value !== 'canceled')
  .map((s) => `${s.value},${s.label}`)
  .join(':');

interface ISearchContext {
  sortBy: (column: string) => void;
  getSortIcon: (column: string) => string;
}

interface IRoleContext {
  hasRole: (roleName: string) => boolean;
}

// export const SearchContext = React.createContext(
//   [{ sortBy: (column: string) => { }, getSortIcon: (column: string) => '' }, (c: ISearchContext) => { }] as [ISearchContext, Dispatch<SetStateAction<ISearchContext>>]);

export const SearchContext = React.createContext<ISearchContext>(
  {} as ISearchContext
);

export const RoleContext = React.createContext<IRoleContext>(
  {} as IRoleContext
);

interface ListProps extends RouteComponentProps<any> {
  history: string[];
  location: { pathname: string; search: string };
  roles: string[];
}

const List = (props: ListProps) => {
  const requestIdRef = useRef(0);

  const urlParams = new URLParamsReader(
    props.location.search,
    new URLSearchParams({ status: DEFAULT_STATUSES }).toString()
  );

  const [settings, setSettings] = useState({
    tasktype: urlParams.getTaskTypeISelect('tasktype'),
    sidx: urlParams.getSidx('prod_deadline'),
    sord: urlParams.getSord('asc'),
    rows: urlParams.getRows(),
    page: urlParams.getPage(),
    priority: urlParams.getISelect('priority'),
    machine: urlParams.getISelect('machine'),
    operator: urlParams.getISelect('operator'),
    timeline: urlParams.getString('timeline'),
    order_id: urlParams.getString('order_id'),
    status: urlParams.getISelectArray('status'),
    prod_deadline_start: urlParams.getString('prod_deadline_start'),
    prod_deadline_end: urlParams.getString('prod_deadline_end'),
  });

  const [selectedItems, setSelectedItems] = useState(new Array<number>()),
    [totalLength, setTotalLength] = useState(0),
    [projectsTasks, setProjectsTasks] = useState(new Array<ProjectTaskData>()),
    [lastPage, setLastPage] = useState(1),
    [taskTypes, setTaskTypes] = useState(new Array<ISelectOption>()),
    [machines, setMachines] = React.useState<ISelectOption[]>([]),
    [operators, setOperators] = React.useState<ISelectOption[]>([]),
    [allChecked, setAllChecked] = useState(false),
    handleCheckboxClick = (projectTask: ProjectTaskData, checked: boolean) => {
      if (checked) {
        setSelectedItems([...selectedItems, projectTask.id]);
      } else {
        setSelectedItems(selectedItems.filter((n) => n != projectTask.id));
      }
    },
    onCheckboxAllClick = () => {
      if (selectedItems.length === projectsTasks.length) {
        setSelectedItems([]);
      } else {
        setSelectedItems(projectsTasks.map((pt) => pt.id));
      }
    },
    handleRefreshClick = () => {
      loadData();
    },
    handleNextPageClick = () => {
      if (settings.page < lastPage) {
        setSettings({ ...settings, page: settings.page + 1 });
      }
    },
    handlePrevPageClick = () => {
      if (settings.page > 1) {
        setSettings({ ...settings, page: settings.page - 1 });
      }
    },
    onRowsChange = (newRows: number) => {
      setSettings({ ...settings, page: 1, rows: newRows });
    };

  function loadMachines() {
    Axios({ url: `/machines.json` })
      .then((res) => {
        setMachines(
          res.data.map((m) => ({ label: m.name, value: m.id.toString() }))
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }

  function loadOperators(machineId?: string) {
    const url = machineId
      ? `/operatorships.json?machine=${machineId}`
      : `/operatorships.json`;
    Axios({ url })
      .then((res) => {
        setOperators(
          res.data.map((o) => ({ label: o.name, value: o.user_id.toString() }))
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }

  function sortBy(column: string): void {
    if (settings.sidx === column) {
      const newdir = settings.sord === 'desc' ? 'asc' : 'desc';
      setSettings({ ...settings, sord: newdir });
    } else {
      setSettings({ ...settings, sidx: column });
    }

  }

  function getSortIcon(column: string): string {
    if (column === settings.sidx) {
      return settings.sord === 'desc' ? 'fa fa-sort-down' : 'fa fa-sort-up';
    } else {
      return 'fa fa-sort';
    }
  }

  function recalcLastPage(): void {
    setLastPage(Math.ceil(totalLength / settings.rows));
  }

  function loadFiltersData() {
    Axios({ url: `/task_management/fdata.json` })
      .then((res) => {
        setTaskTypes(res.data.tasktypes);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  function prepareQuery(): string {
    let url_params = `rows=${settings.rows}&page=${settings.page}&sidx=${settings.sidx}&sord=${settings.sord}`;

    if (settings.tasktype) {
      url_params = `${url_params}&tasktype=${settings.tasktype.value},${encodeURIComponent(settings.tasktype.label)}`;
    }

    if (settings.status.length > 0) {
      const st_all = settings.status.map((st) => `${st.value},${st.label}`).join(':');
      url_params = `${url_params}&status=${st_all}`;
    }

    if (settings.priority) {
      url_params = `${url_params}&priority=${settings.priority.value},${settings.priority.label}`;
    }

    if (settings.operator) {
      url_params = `${url_params}&operator=${settings.operator.value},${settings.operator.label}`;
    }
    if (settings.machine) {
      url_params = `${url_params}&machine=${settings.machine.value},${settings.machine.label}`;
    }
    if (`${settings.timeline}`.length > 0) {
      url_params = `${url_params}&timeline=${settings.timeline}`;
    }
    if (`${settings.order_id}`.length > 0) {
      url_params = `${url_params}&order_id=${encodeURIComponent(settings.order_id)}`;
    }
    if (`${settings.prod_deadline_start}`.length > 0) {
      url_params = `${url_params}&prod_deadline_start=${settings.prod_deadline_start}`;
    }
    if (`${settings.prod_deadline_end}`.length > 0) {
      url_params = `${url_params}&prod_deadline_end=${settings.prod_deadline_end}`;
    }

    return url_params;
  }

  function selectedMaterialM3(): number {
    return selectedItems.reduce((sum, id) => {
      const task = projectsTasks.find((pt) => pt.id === id);
      return sum + (task && task.project ? task.project.material_m3 || 0 : 0);
    }, 0);
  }

  function selectedAreAllTodo(): boolean {
    return (
      selectedItems.length > 0 &&
      selectedItems.every((id) => {
        const task = projectsTasks.find((pt) => pt.id === id);
        return task && task.status === 'todo';
      })
    );
  }

  function exportData() {
    const url_params = prepareQuery();
    Axios({
      method: 'POST',
      url: `/task_management/export_to_xls.xlsx?${url_params}`,
      responseType: 'blob',
      data: {
        project_tasks_ids: selectedItems,
        tasktype: settings.tasktype ? settings.tasktype.value : '',
      },
      headers: {
        accept: 'application/xlsx',
        'X-CSRF-Token': Rails.csrfToken(),
      },
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'tasks.xlsx');
        document.body.appendChild(link);
        link.click();

        // export marks the exported tasks as waiting, so refresh their statuses
        loadData();
      })
      .catch((err) => {
        console.log(err);
      });
  }

  function loadData() {
    let url_params = prepareQuery();
    props.history.push(`${props.location.pathname}?${url_params}`);

    const requestId = ++requestIdRef.current;

    Axios({
      method: 'GET',
      url: `/task_management/data.json?${url_params}`,
      headers: {
        accept: 'application/json',
      },
    })
      .then((res) => {
        if (requestId !== requestIdRef.current) {
          // a newer request has since been issued; ignore this stale response
          return;
        }
        if (res.data.project_tasks.length > 0) {
          setProjectsTasks(res.data.project_tasks);
          setTotalLength(res.data.records);
        } else {
          setProjectsTasks([]);
          setTotalLength(0);
        }

        // if (settings.page > 1 && settings.page > lastPage) {
        //   setSettings({ ...settings, page: 1 })
        // }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const handlePageClick = (event) => {
    setSettings({ ...settings, page: event.selected + 1 });
  };

  function handleOperatorChange(opt: ISelectOption): void {
    setSettings({ ...settings, operator: opt ?? null, page: 1 });
  }

  function handleMachineChange(opt: ISelectOption): void {
    setSettings({ ...settings, machine: opt ?? null, operator: null as unknown as ISelectOption, page: 1 });
    loadOperators(opt ? opt.value : undefined);
  }

  function searchByTimeline(newVal: string) {
    if (settings.timeline !== newVal) {
      setSettings({ ...settings, timeline: newVal });
    }
  }

  function searchByOrderId(newVal: string) {
    if (settings.order_id !== newVal) {
      setSettings({ ...settings, order_id: newVal, page: 1 });
    }
  }

  function hasRole(roleName: string) {
    return props.roles.includes(roleName);
  }

  const isOperatorView = hasRole('pm_operator') && !hasRole('super_admin');

  function visibleItems(): React.ReactNode[] {
    return projectsTasks.map((n) => (
      <TaskRow
        key={n.id}
        projectTask={n}
        allMachines={machines}
        isChecked={
          selectedItems.find((i) => {
            return i === n.id || false;
          }) !== undefined
        }
        onCheckboxClick={handleCheckboxClick}
        onTruckUpdated={loadData}
      />
    ));
  }

  function handlePriorityChange(opt: ISelectOption): void {
    if (opt) {
      setSettings({ ...settings, priority: opt, page: 1 });
    } else {
      setSettings({ ...settings, priority: null, page: 1 });
    }
  }

  function handleTaskTypeChange(opt: ISelectOption): void {
    if (opt) {
      setSettings({ ...settings, tasktype: opt, page: 1 });
    } else {
      setSettings({ ...settings, tasktype: null, page: 1 });
    }
  }

  function handleStatusChange(opt: ISelectOption[]): void {
    setSettings({ ...settings, status: opt || [], page: 1 });
  }

  function handleProdDeadlineRangeChange(dates: [Date | null, Date | null]): void {
    setSettings({
      ...settings,
      page: 1,
      prod_deadline_start: dates[0] ? formatDate(dates[0]) : '',
      prod_deadline_end: dates[1] ? formatDate(dates[1]) : '',
    });
  }

  useEffect(() => {
    setAllChecked(projectsTasks.length > 0 && selectedItems.length === projectsTasks.length);
  }, [selectedItems, projectsTasks]);

  useEffect(() => {
    recalcLastPage();
  }, [totalLength, settings]);

  useEffect(() => {
    // selection from the previous filters/page is no longer visible, so drop it
    setSelectedItems([]);
    loadData();
  }, [settings]);

  useEffect(() => {
    loadFiltersData();
    loadMachines();
    loadOperators();
  }, []);

  return (
    <div className="wrapper wrapper-content">
      <div className="ibox-content m-b-sm border-bottom">
        <div className="row">
          <div className="col-lg-12 animated fadeInRight">
            <h2>Tasks ({totalLength})</h2>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-2">
            <div className="form-group">
              <Select
                isClearable
                placeholder="Task Type"
                options={taskTypes}
                value={
                  settings.tasktype
                    ? taskTypes.find((tt) => String(tt.value) === String(settings.tasktype.value)) || settings.tasktype
                    : null
                }
                onChange={(val) => handleTaskTypeChange(val)}
              />
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <Select
                isClearable
                placeholder="Machine"
                options={machines}
                defaultValue={settings.machine}
                onChange={(val) => handleMachineChange(val)}
              />
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <Select
                key={settings.machine ? settings.machine.value : 'all'}
                isClearable
                placeholder="Operator"
                options={operators}
                defaultValue={settings.operator}
                onChange={(val) => handleOperatorChange(val)}
              />
            </div>
          </div>
          <div className="col-sm-2">
            <TextSearchField
              inputText={settings.timeline}
              placeholder={'Timeline'}
              searchBy={searchByTimeline}
            ></TextSearchField>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <Select
                isClearable
                placeholder="Priority"
                options={AllPriorities}
                defaultValue={settings.priority}
                onChange={(val) => handlePriorityChange(val)}
              />
            </div>
          </div>

          <div className="col-sm-2">
            <div className="form-group">
              <Select
                isMulti
                placeholder="Status"
                options={AllProgressStatuses}
                defaultValue={settings.status}
                onChange={(val) => handleStatusChange(val)}
              />
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group">
              <DateRangeSelect
                label="Prod Deadline"
                endDate={
                  settings.prod_deadline_end
                    ? new Date(settings.prod_deadline_end.replaceAll('-', '/'))
                    : null
                }
                startDate={
                  settings.prod_deadline_start
                    ? new Date(settings.prod_deadline_start.replaceAll('-', '/'))
                    : null
                }
                onDatesChange={handleProdDeadlineRangeChange}
              />
            </div>
          </div>
          <div className="col-sm-2">
            <label>Order ID</label>
            <TextSearchField
              inputText={settings.order_id}
              placeholder={'Order ID'}
              searchBy={searchByOrderId}
            ></TextSearchField>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12 animated fadeInRight">
            <Paging
              onRowsChange={onRowsChange}
              onNextClick={handleNextPageClick}
              onPrevClick={handlePrevPageClick}
              rows={settings.rows}
              page={settings.page}
              lastPage={lastPage}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12 animated fadeInRight">
          <div className="mail-box scrollable-table-box">
            <table className="footable table table-hover table-mail footable-loaded sticky-header-footer">
              <SearchContext.Provider
                value={{ sortBy: sortBy, getSortIcon: getSortIcon }}
              >
                <TableHeader
                  isChecked={allChecked}
                  onCheckboxAllClick={onCheckboxAllClick}
                  isOperatorView={isOperatorView}
                />
              </SearchContext.Provider>
              <tbody>
                <RoleContext.Provider value={{ hasRole: hasRole }}>
                  {visibleItems()}
                </RoleContext.Provider>
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan={9}>
                    <Button
                      variant="outlined"
                      disabled={
                        selectedItems.length === 0 ||
                        settings.tasktype === null ||
                        settings.tasktype === undefined ||
                        !selectedAreAllTodo()
                      }
                      onClick={exportData}
                    >
                      Export to XLS{' '}
                      {selectedItems.length > 0
                        ? `(${selectedItems.length})`
                        : ''}
                    </Button>
                    {selectedItems.length > 0 && (
                      <span className="m-l-sm">
                        m<sup>3</sup>: {formatMoney(selectedMaterialM3(), 2, '.', ',')}
                      </span>
                    )}
                  </td>
                  <td colSpan={9} className="footable-visible">
                    <ReactPaginate
                      id="react-paginate"
                      // labels

                      previousLabel={'Previous'}
                      nextLabel={'Next'}
                      breakLabel={'…'}
                      disabledClassName={'disabled'}
                      breakClassName={'page-item'}
                      breakLinkClassName={'page-link'}
                      containerClassName={'pagination pull-right'}
                      pageClassName={'page-item'}
                      pageLinkClassName={'page-link'}
                      previousClassName={'page-item'}
                      previousLinkClassName={'page-link'}
                      nextClassName={'page-item'}
                      nextLinkClassName={'page-link'}
                      activeClassName={'active'}
                      // functionality
                      initialPage={settings.page - 1}
                      pageCount={lastPage} //total pages
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={5}
                      //   forcePage={5}
                      onPageChange={
                        (event) => {
                          handlePageClick(event);
                        } /*Fxn when page clicked*/
                      }
                      disableInitialCallback={true}
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default List;
